import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ApiError,
  connectAccount,
  fetchOauthStatus,
  oauthStart,
  suggestAutoconfig,
} from '../lib/api'
import { FieldLabel } from './FieldInfo'
import { Icon } from './Icon'
import { LanguageMenu } from './LanguageMenu'

type Props = {
  onBack: () => void
  onConnected?: () => void
  onAskAgent?: () => void
}

/** Add mailbox: email + IMAP/SMTP. OAuth only as compact secondary links. */
export function AddAccountPanel({ onBack, onConnected, onAskAgent }: Props) {
  const { t } = useTranslation()
  const [oauthOk, setOauthOk] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authUsername, setAuthUsername] = useState('')
  const [imapHost, setImapHost] = useState('')
  const [imapPort, setImapPort] = useState(993)
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState(587)
  const [linkedIds, setLinkedIds] = useState<string[]>([])

  useEffect(() => {
    void fetchOauthStatus()
      .then((h) => setOauthOk(new Set((h.providers_enabled || []).map((p) => p.toLowerCase()))))
      .catch(() => setOauthOk(new Set()))
  }, [])

  async function startOauth(provider: 'microsoft' | 'gmail') {
    setError(null)
    setBusy(provider)
    try {
      const res = await oauthStart(provider)
      window.location.assign(res.authorize_url)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.oauthFailed'))
      setBusy(null)
    }
  }

  async function onEmailBlur() {
    if (!email.includes('@')) return
    try {
      const s = await suggestAutoconfig(email.trim())
      if (s.imap_host) setImapHost(s.imap_host)
      if (s.imap_port) setImapPort(s.imap_port)
      if (s.smtp_host) setSmtpHost(s.smtp_host)
      if (s.smtp_port) setSmtpPort(s.smtp_port === 465 ? 587 : s.smtp_port)
      if (s.oauth_preferred) setNote(t('auth.oauthPreferred'))
    } catch {
      /* ignore */
    }
  }

  async function onConnectImap() {
    setError(null)
    setNote(null)
    const em = email.trim()
    const pass = password
    const ih = imapHost.trim()
    const sh = smtpHost.trim()
    if (!em.includes('@')) {
      setError(t('auth.enterEmail'))
      return
    }
    if (!pass) {
      setError(t('auth.appPassword'))
      return
    }
    if (!ih || !sh) {
      setError(t('auth.hostsRequired'))
      return
    }
    setBusy('imap')
    try {
      const res = await connectAccount({
        email: em,
        password: pass,
        auth_username: authUsername.trim() || undefined,
        imap_host: ih,
        imap_port: imapPort,
        smtp_host: sh,
        smtp_port: smtpPort === 465 ? 587 : smtpPort,
        acknowledge_warning: true,
      })
      setLinkedIds((x) => [...x, res.id])
      setNote(t('auth.imapConnected'))
      setPassword('')
      onConnected?.()
    } catch (err) {
      if (err instanceof ApiError) {
        const code = err.message
        if (code === 'imap_auth_failed') setError(t('auth.imapAuthFailed'))
        else if (code === 'imap_timeout') setError(t('auth.imapTimeout'))
        else if (code === 'smtp_ssl') setError(t('auth.smtpSsl'))
        else if (code === 'smtp_timeout') setError(t('auth.smtpTimeout'))
        else if (code === 'smtp_failed' || /smtp_failed/i.test(code)) setError(t('auth.smtpFailed'))
        else setError(code || t('auth.imapConnectFailed'))
      } else {
        setError(t('auth.imapConnectFailed'))
      }
    } finally {
      setBusy(null)
    }
  }

  const showOauth = oauthOk.has('gmail') || oauthOk.has('microsoft')

  return (
    <section className="rd-section active" data-sec-panel="add">
      <div className="rd-panel-head rd-panel-head-inline">
        <button type="button" className="rd-back always" onClick={onBack} aria-label={t('auth.cancel')}>
          <Icon name="arrow_back" size={20} />
        </button>
        <h2>{t('mail.addAccount')}</h2>
        <div className="rd-panel-head-lang">
          {/* Same control as inbox list header — compact mail-pill is white-on-white here */}
          <LanguageMenu />
        </div>
      </div>
      <div className="rd-scroll">
        <div className="rd-sec-body">
          <p className="rd-sec-notice">{t('auth.accountsLede')}</p>
          <p className="rd-sync-cap-note" role="note">
            {t('mail.syncInboxCapNote')}
          </p>

          {linkedIds.length ? (
            <p className="ok" style={{ color: 'var(--rd-ok)' }}>
              ✓ {t('auth.connected')} ({linkedIds.length})
            </p>
          ) : null}

          <div className="rd-card">
            <FieldLabel htmlFor="rd-acct-email" info={t('auth.tipEmail')}>
              {t('auth.enterEmail')}
            </FieldLabel>
            <div className="rd-emailrow" style={{ marginBottom: 12 }}>
              <input
                id="rd-acct-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => void onEmailBlur()}
                placeholder="name@example.com"
                autoComplete="username"
              />
              <button type="button" onClick={() => void onEmailBlur()}>
                {t('auth.recoveryContinue')}
              </button>
            </div>

            <FieldLabel htmlFor="rd-acct-pass" info={t('auth.tipPassword')}>
              {t('auth.appPassword')}
            </FieldLabel>
            <input
              id="rd-acct-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              autoComplete="current-password"
              className="rd-search"
              style={{ margin: '0 0 12px', width: '100%' }}
            />

            <FieldLabel htmlFor="rd-acct-authuser" info={t('auth.tipAuthUsername')}>
              {t('auth.authUsername')}
            </FieldLabel>
            <input
              id="rd-acct-authuser"
              type="text"
              value={authUsername}
              onChange={(e) => setAuthUsername(e.target.value)}
              placeholder={t('auth.authUsernamePlaceholder')}
              autoComplete="off"
              className="rd-search"
              style={{ margin: '0 0 12px', width: '100%' }}
            />

            <details open={!imapHost || !smtpHost}>
              <summary>{t('auth.advancedImap')}</summary>
              <FieldLabel info={t('auth.tipImapHost')}>IMAP host</FieldLabel>
              <input
                className="rd-search"
                style={{ margin: '4px 0 8px', width: '100%' }}
                value={imapHost}
                onChange={(e) => setImapHost(e.target.value)}
                placeholder="mail.example.com"
              />
              <FieldLabel info={t('auth.tipImapPort')}>IMAP port</FieldLabel>
              <input
                className="rd-search"
                style={{ margin: '4px 0 8px', width: '100%' }}
                type="number"
                value={imapPort}
                onChange={(e) => setImapPort(Number(e.target.value) || 993)}
              />
              <FieldLabel info={t('auth.tipSmtpHost')}>SMTP host</FieldLabel>
              <input
                className="rd-search"
                style={{ margin: '4px 0 8px', width: '100%' }}
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="mail.example.com"
              />
              <FieldLabel info={t('auth.tipSmtpPort')}>SMTP port</FieldLabel>
              <input
                className="rd-search"
                style={{ margin: '4px 0 8px', width: '100%' }}
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(Number(e.target.value) || 587)}
              />
            </details>

            <button
              type="button"
              className="rd-compose"
              style={{ margin: '12px 0 0', width: '100%' }}
              disabled={Boolean(busy)}
              onClick={() => void onConnectImap()}
            >
              {busy === 'imap' ? t('auth.working') : t('auth.connectImap')}
            </button>
          </div>

          {showOauth ? (
            <p className="d rd-oauth-inline">
              {t('auth.orOauth')}{' '}
              {oauthOk.has('gmail') ? (
                <button
                  type="button"
                  className="rd-linkish"
                  disabled={Boolean(busy)}
                  onClick={() => void startOauth('gmail')}
                >
                  {busy === 'gmail' ? t('auth.working') : 'Gmail'}
                </button>
              ) : null}
              {oauthOk.has('gmail') && oauthOk.has('microsoft') ? ' · ' : null}
              {oauthOk.has('microsoft') ? (
                <button
                  type="button"
                  className="rd-linkish"
                  disabled={Boolean(busy)}
                  onClick={() => void startOauth('microsoft')}
                >
                  {busy === 'microsoft' ? t('auth.working') : 'Microsoft'}
                </button>
              ) : null}
            </p>
          ) : null}

          {onAskAgent ? (
            <div className="rd-ask-agent" role="note">
              <p>{t('mail.connectHelpHint')}</p>
              <button type="button" className="rd-ask-agent-btn" onClick={onAskAgent}>
                {t('mail.connectHelpCta')}
              </button>
            </div>
          ) : null}

          <div className="rd-card" style={{ marginTop: 14, borderStyle: 'dashed' }}>
            <h3>{t('auth.create7gcTitle')}</h3>
            <div className="d">{t('auth.create7gcLede')}</div>
            <button type="button" className="rd-compose" style={{ margin: 0, width: '100%' }} disabled>
              {t('auth.create7gcCta')}
            </button>
          </div>

          {error ? (
            <p role="alert" style={{ color: 'var(--rd-danger)' }}>
              {error}
            </p>
          ) : null}
          {note ? <p className="d">{note}</p> : null}
        </div>
      </div>
    </section>
  )
}
